import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, UserPlus } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

const OfficeCreateClient = ({ userData, setUserData }) => {
    const [activeSubTab, setActiveSubTab] = useState("clients");
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [officeData, setOfficeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [newClientData, setNewClientData] = useState({
        name: "",
        email: "",
        phone: "",
    });

    useEffect(() => {
        const fetchOffice = async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) {
                setError("Authentication required.");
                setLoading(false);
                return;
            }

            try {
                const response = await api.get("/offices", {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data.success && response.data.data) {
                    setOfficeData(response.data.data);
                } else {
                    setError("No office data found.");
                }
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to load office data.";
                setError(msg);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: msg,
                    confirmButtonColor: "#1e40af",
                });
            } finally {
                setLoading(false);
            }
        };

        if (userData.isLawFirm) {
            fetchOffice();
        } else {
            setLoading(false);
        }
    }, [userData.isLawFirm]);

    const handleAddClient = async (e) => {
        e.preventDefault();
        setSaving(true);

        const token = localStorage.getItem("auth_token");
        if (!token) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Authentication required.",
                confirmButtonColor: "#1e40af",
            });
            setSaving(false);
            return;
        }

        const payload = {
            name: newClientData.name.trim(),
            email: newClientData.email.trim(),
            phone: newClientData.phone.trim(),
        };

        try {
            const response = await api.post("/clients", payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.data.success && response.data.data) {
                const newClient = response.data.data;

                // إضافة العميل للقائمة (لو فيه clients في الـ office)
                setOfficeData((prev) => ({
                    ...prev,
                    clients: [...(prev.clients || []), newClient],
                }));

                setShowAddClientModal(false);
                setNewClientData({ name: "", email: "", phone: "" });

                // رسالة نجاح مع كلمة المرور المؤقتة
                Swal.fire({
                    icon: "success",
                    title: "Client Added!",
                    html: `
                        <p class="text-sm">Account created successfully.</p>
                        <p class="text-sm mt-2">Verification email sent to:</p>
                        <p class="font-medium">${newClient.email}</p>
                        <div class="mt-3 p-3 bg-blue-50 rounded-lg text-left">
                            <p class="text-xs font-medium text-blue-900">Temporary Password:</p>
                            <p class="font-mono text-sm text-blue-700">${newClient.temporary_password}</p>
                            <p class="text-xs text-blue-600 mt-1">Will be sent via email</p>
                        </div>
                    `,
                    confirmButtonColor: "#1e40af",
                    timer: 6000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to create client.";
            Swal.fire({
                icon: "error",
                title: "Error",
                text: msg,
                confirmButtonColor: "#1e40af",
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <p className="text-red-700 font-medium">{error}</p>
            </div>
        );
    }

    const clients = officeData?.clients || [];

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Law Firm Dashboard</h2>
                    <p className="text-gray-600 mt-1">Manage your clients</p>
                </div>
            </div>

            {/* Sub Navigation */}
            <div className="mb-6">
                <nav className="flex space-x-8">
                    {[{ id: "clients", label: "Clients" }].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveSubTab(tab.id)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeSubTab === tab.id
                                ? "border-blue-500 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Clients List */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Firm Clients ({clients.length})
                    </h3>
                    <motion.button
                        onClick={() => setShowAddClientModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <UserPlus className="w-4 h-4" />
                        Add Client
                    </motion.button>
                </div>
            </div>

            {/* Add Client Modal */}
            {showAddClientModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-xl p-6 w-full max-w-md"
                    >
                        <h3 className="text-xl font-semibold text-gray-900 mb-5">Create New Client</h3>
                        <form onSubmit={handleAddClient} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                <input
                                    type="text"
                                    value={newClientData.name}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="John Client"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                                <input
                                    type="email"
                                    value={newClientData.email}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="client@example.com"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                                <input
                                    type="tel"
                                    value={newClientData.phone}
                                    onChange={(e) => setNewClientData(prev => ({ ...prev, phone: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="+1234567890"
                                    required
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAddClientModal(false)}
                                    disabled={saving}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 disabled:opacity-70`}
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="w-4 h-4" />
                                            Create Client
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default OfficeCreateClient;